import React, { useState } from 'react';
import { BarChart2, TrendingUp, Clock, Maximize, PieChart, ChevronDown, ChevronUp } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { formatNumber } from '../../../../../utils/formatting';

const InvestmentChart = ({ 
  investmentData, 
  results, 
  formData,
  cryptocurrencyTypes,
  investmentCurrencies
}) => {
  const [chartType, setChartType] = useState('growth');
  const [showDetails, setShowDetails] = useState(false);
  
  if (!investmentData || !investmentData.monthly || investmentData.monthly.length === 0) {
    return null;
  }
  
  const getCurrencySymbol = (currencyType) => {
    const currency = investmentCurrencies.find(c => c.id === currencyType);
    return currency ? currency.symbol : '₽';
  };
  
  const currencySymbol = getCurrencySymbol(formData.investmentCurrency);
  const cryptoName = cryptocurrencyTypes.find(c => c.id === formData.cryptocurrencyType)?.name || formData.cryptocurrencyType;
  
  // Подготовка данных для графика
  const prepareChartData = () => {
    if (chartType === 'growth') {
      // Данные для графика роста инвестиций
      return investmentData.monthly.map(item => ({
        month: item.month,
        investment: item.investment,
        baseline: investmentData.baseline[item.month - 1]?.investment || results.initialInvestment,
        pessimistic: results.hasScenariosAnalysis ? investmentData.pessimistic[item.month - 1]?.investment || results.initialInvestment : null,
        optimistic: results.hasScenariosAnalysis ? investmentData.optimistic[item.month - 1]?.investment || results.initialInvestment : null,
      }));
    } else if (chartType === 'profit') {
      // Данные для графика прибыли
      return investmentData.monthly.map(item => ({
        month: item.month,
        profit: item.profit,
        cumulative: item.cumulative.profit,
      }));
    } else if (chartType === 'additions') {
      // Данные для графика дополнительных инвестиций и траншей
      return investmentData.monthly.map(item => ({
        month: item.month,
        additional: item.additional,
        tranche: item.tranche,
        reinvested: item.reinvested,
      }));
    } else {
      // Данные для графика затрат
      return investmentData.monthly.map(item => ({
        month: item.month,
        fees: item.fees,
        tax: item.tax,
        cumulative_fees: item.cumulative.fees,
        cumulative_tax: item.cumulative.tax,
      }));
    }
  };
  
  const chartData = prepareChartData();
  
  // Настройка графика в зависимости от типа
  const getChartConfig = () => {
    switch(chartType) {
      case 'growth':
        return {
          title: 'Динамика роста инвестиции',
          lines: [
            { key: 'investment', name: `${cryptoName}: Фактический рост`, color: '#6366f1' },
            { key: 'baseline', name: 'Базовый сценарий', color: '#94a3b8' },
            ...(results.hasScenariosAnalysis ? [
              { key: 'pessimistic', name: 'Пессимистичный сценарий', color: '#ef4444' },
              { key: 'optimistic', name: 'Оптимистичный сценарий', color: '#22c55e' }
            ] : [])
          ],
          yAxisLabel: `Стоимость (${currencySymbol})`,
          tooltipFormatter: (value) => `${formatNumber(value, 0)} ${currencySymbol}`
        };
      case 'profit':
        return {
          title: 'Прибыль по месяцам',
          lines: [
            { key: 'profit', name: 'Прибыль за месяц', color: '#22c55e' },
            { key: 'cumulative', name: 'Накопленная прибыль', color: '#0ea5e9', strokeWidth: 2 }
          ],
          yAxisLabel: `Прибыль (${currencySymbol})`,
          tooltipFormatter: (value) => `${formatNumber(value, 0)} ${currencySymbol}`
        };
      case 'additions':
        return {
          title: 'Дополнительные инвестиции',
          lines: [
            { key: 'additional', name: 'Регулярные вложения', color: '#8b5cf6' },
            { key: 'tranche', name: 'Транши', color: '#ec4899' },
            { key: 'reinvested', name: 'Реинвестировано', color: '#14b8a6' }
          ],
          yAxisLabel: `Сумма (${currencySymbol})`,
          tooltipFormatter: (value) => `${formatNumber(value, 0)} ${currencySymbol}`
        };
      case 'costs':
      default:
        return {
          title: 'Комиссии и налоги',
          lines: [
            { key: 'fees', name: 'Комиссии за месяц', color: '#f97316' },
            { key: 'tax', name: 'Налоги за месяц', color: '#ef4444' },
            { key: 'cumulative_fees', name: 'Накопленные комиссии', color: '#fb923c', strokeWidth: 2 },
            { key: 'cumulative_tax', name: 'Накопленные налоги', color: '#f87171', strokeWidth: 2 }
          ],
          yAxisLabel: `Сумма (${currencySymbol})`,
          tooltipFormatter: (value) => `${formatNumber(value, 0)} ${currencySymbol}`
        };
    }
  };

  const chartConfig = getChartConfig();
  
  // Кастомный тултип для графика
  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) {
      return null;
    }
    
    return (
      <div className="bg-white dark:bg-gray-800 p-3 border border-gray-200 dark:border-gray-700 rounded-md shadow-lg">
        <p className="text-sm font-medium text-gray-900 dark:text-white mb-1">Месяц: {label}</p>
        {payload.map((entry, index) => (
          <div key={`tooltip-${index}`} className="flex items-center gap-2 text-xs">
            <div style={{ width: 12, height: 12, backgroundColor: entry.color, borderRadius: '50%' }}></div>
            <span style={{ color: entry.color }} className="font-medium">{entry.name}:</span>
            <span className="text-gray-700 dark:text-gray-300">{chartConfig.tooltipFormatter(entry.value)}</span>
          </div>
        ))}
      </div>
    );
  };
  
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm p-5 mt-8">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center">
          <BarChart2 className="w-5 h-5 mr-2 text-indigo-500" />
          {chartConfig.title}
        </h3>

        {/* Кнопка переключения детализации */}
        <button
          className="text-sm flex items-center gap-1 text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 transition-colors"
          onClick={() => setShowDetails(!showDetails)}
        >
          {showDetails ? (
            <>
              <ChevronUp className="w-4 h-4" />
              Скрыть детали
            </>
          ) : (
            <>
              <ChevronDown className="w-4 h-4" />
              Показать детали
            </>
          )}
        </button>
      </div>

      {/* Переключатели типа графика */}
      <div className="flex space-x-1 border-b border-gray-200 dark:border-gray-700 mb-4 overflow-x-auto pb-1">
        <button
          onClick={() => setChartType('growth')}
          className={`py-2 px-3 text-sm font-medium rounded-t-lg whitespace-nowrap flex items-center gap-1 ${
            chartType === 'growth'
              ? 'text-indigo-600 dark:text-indigo-400 border-b-2 border-indigo-500 dark:border-indigo-400'
              : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
          }`}
        >
          <TrendingUp className="w-4 h-4" />
          Рост
        </button>
        <button
          onClick={() => setChartType('profit')}
          className={`py-2 px-3 text-sm font-medium rounded-t-lg whitespace-nowrap flex items-center gap-1 ${
            chartType === 'profit'
              ? 'text-indigo-600 dark:text-indigo-400 border-b-2 border-indigo-500 dark:border-indigo-400'
              : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
          }`}
        >
          <PieChart className="w-4 h-4" />
          Прибыль
        </button>
        <button
          onClick={() => setChartType('additions')}
          className={`py-2 px-3 text-sm font-medium rounded-t-lg whitespace-nowrap flex items-center gap-1 ${
            chartType === 'additions'
              ? 'text-indigo-600 dark:text-indigo-400 border-b-2 border-indigo-500 dark:border-indigo-400'
              : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
          }`}
        >
          <Maximize className="w-4 h-4" />
          Вложения
        </button>
        <button
          onClick={() => setChartType('costs')}
          className={`py-2 px-3 text-sm font-medium rounded-t-lg whitespace-nowrap flex items-center gap-1 ${
            chartType === 'costs'
              ? 'text-indigo-600 dark:text-indigo-400 border-b-2 border-indigo-500 dark:border-indigo-400'
              : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
          }`}
        >
          <Clock className="w-4 h-4" />
          Расходы
        </button>
      </div>

      {/* График */}
      <div className="h-80 crypto-chart">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart
            data={chartData}
            margin={{
              top: 5,
              right: 30,
              left: 20,
              bottom: 5,
            }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.1} />
            <XAxis 
              dataKey="month" 
              label={{ value: 'Месяц', position: 'insideBottomRight', offset: -10 }} 
              tick={{ fontSize: 12 }}
            />
            <YAxis 
              label={{ value: chartConfig.yAxisLabel, angle: -90, position: 'insideLeft', style: { textAnchor: 'middle' } }} 
              tick={{ fontSize: 12 }}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend />
            {chartConfig.lines.map((line, index) => (
              <Line
                key={index}
                type="monotone"
                dataKey={line.key}
                name={line.name}
                stroke={line.color}
                strokeWidth={line.strokeWidth || 1.5}
                dot={{ r: 3 }}
                activeDot={{ r: 5 }}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Детализированная таблица */}
      {showDetails && (
        <div className="mt-4 overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-800">
              <tr>
                <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Месяц
                </th>
                {chartType === 'growth' && (
                  <>
                    <th scope="col" className="px-3 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Стоимость
                    </th>
                    <th scope="col" className="px-3 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Базовый сценарий
                    </th>
                    {results.hasScenariosAnalysis && (
                      <>
                        <th scope="col" className="px-3 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Пессимистичный
                        </th>
                        <th scope="col" className="px-3 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Оптимистичный
                        </th>
                      </>
                    )}
                  </>
                )}
                {chartType === 'profit' && (
                  <>
                    <th scope="col" className="px-3 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Прибыль за месяц
                    </th>
                    <th scope="col" className="px-3 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Накопленная прибыль
                    </th>
                  </>
                )}
                {chartType === 'additions' && (
                  <>
                    <th scope="col" className="px-3 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Регулярные вложения
                    </th>
                    <th scope="col" className="px-3 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Транши
                    </th>
                    <th scope="col" className="px-3 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Реинвестировано
                    </th>
                  </>
                )}
                {chartType === 'costs' && (
                  <>
                    <th scope="col" className="px-3 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Комиссии
                    </th>
                    <th scope="col" className="px-3 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Налоги
                    </th>
                    <th scope="col" className="px-3 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Всего комиссий
                    </th>
                    <th scope="col" className="px-3 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Всего налогов
                    </th>
                  </>
                )}
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {chartData.map((item, index) => (
                <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                  <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                    {item.month}
                  </td>
                  {chartType === 'growth' && (
                    <>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-right text-gray-900 dark:text-gray-100">
                        {formatNumber(item.investment, 0)} {currencySymbol}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-right text-gray-500 dark:text-gray-400">
                        {formatNumber(item.baseline, 0)} {currencySymbol}
                      </td>
                      {results.hasScenariosAnalysis && (
                        <>
                          <td className="px-3 py-2 whitespace-nowrap text-sm text-right text-red-600 dark:text-red-400">
                            {formatNumber(item.pessimistic, 0)} {currencySymbol}
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap text-sm text-right text-green-600 dark:text-green-400">
                            {formatNumber(item.optimistic, 0)} {currencySymbol}
                          </td>
                        </>
                      )}
                    </>
                  )}
                  {chartType === 'profit' && (
                    <>
                      <td className={`px-3 py-2 whitespace-nowrap text-sm text-right ${item.profit >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                        {formatNumber(item.profit, 0)} {currencySymbol}
                      </td>
                      <td className={`px-3 py-2 whitespace-nowrap text-sm text-right ${item.cumulative >= 0 ? 'text-blue-600 dark:text-blue-400' : 'text-red-600 dark:text-red-400'}`}>
                        {formatNumber(item.cumulative, 0)} {currencySymbol}
                      </td>
                    </>
                  )}
                  {chartType === 'additions' && (
                    <>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-right text-purple-600 dark:text-purple-400">
                        {formatNumber(item.additional, 0)} {currencySymbol}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-right text-pink-600 dark:text-pink-400">
                        {formatNumber(item.tranche, 0)} {currencySymbol}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-right text-teal-600 dark:text-teal-400">
                        {formatNumber(item.reinvested, 0)} {currencySymbol}
                      </td>
                    </>
                  )}
                  {chartType === 'costs' && (
                    <>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-right text-orange-600 dark:text-orange-400">
                        {formatNumber(item.fees, 0)} {currencySymbol}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-right text-red-600 dark:text-red-400">
                        {formatNumber(item.tax, 0)} {currencySymbol}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-right text-orange-600 dark:text-orange-400 font-medium">
                        {formatNumber(item.cumulative_fees, 0)} {currencySymbol}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-right text-red-600 dark:text-red-400 font-medium">
                        {formatNumber(item.cumulative_tax, 0)} {currencySymbol}
                      </td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default InvestmentChart;